import { useTranslation } from 'react-i18next';
import { AuthThemeToggle } from './AuthThemeToggle';
import feltonLogo from '@/assets/icons/logo_felton.svg';

/**
 * The bar across the top of every auth screen: the wordmark, the way in for
 * people without an account, and the theme switch.
 *
 * AL-1792: "Get access" has no destination yet — the layout introduces it but
 * nobody has said where it goes, so it renders disabled rather than lying to
 * the user with a dead link.
 */
export const AuthHeader = () => {
  const { t } = useTranslation();

  return (
    <header className="flex items-center justify-between gap-3 px-4 py-4 sm:px-14 sm:py-5">
      {/* The wordmark is a single near-black path, so it disappears on the dark
          ground. Inverting it is enough — there is no second colour to protect. */}
      {/* The wordmark is wide (about ten times its height), so at 24px it eats a
          phone's whole header. It steps down below sm to leave room for the
          controls on the other side. */}
      {/*
        AL-1846: the halo, in dark only.

        The artwork behind these screens is a flat export, and in dark its lime
        arc comes in from exactly this corner — so the inverted, white wordmark
        crossed it and the first letters vanished into the lime. Reported as the
        logo being "cut off"; measured, the box is the right size and the file is
        a clean transparent wordmark, so nothing is actually clipped. It is
        contrast.

        The arc cannot move (it is baked into the image) and the wordmark has to
        stay white over the dark ground it covers everywhere else, so what is
        left is to separate the two: a soft dark halo, invisible against the dark
        ground and just enough to hold the letters together over the lime. It
        comes after `invert` in the same filter chain, so the shadow is cast by
        the white letters rather than by the black ones the file contains.
      */}
      <img
        src={feltonLogo}
        alt={t('app.auth.brand_name')}
        className="h-[18px] w-auto shrink sm:h-6 dark:filter-[invert(1)_drop-shadow(0_0_5px_hsl(var(--auth-bg)/0.95))]"
      />

      <div className="flex shrink-0 items-center gap-2 sm:gap-4">
        <span className="hidden text-xs leading-4 text-auth-body sm:inline">
          {t('app.auth.need_an_account')}
        </span>

        {/*
          The destination lives in the translations rather than here: the landing
          page has one address per language, so the active locale already picks
          the right one, and the br → pt fallback comes for free.
          Opens in a new tab so a half-typed email is not thrown away.
        */}
        <a
          href={t('app.auth.get_access_url')}
          target="_blank"
          rel="noopener noreferrer"
          className="whitespace-nowrap rounded-full bg-auth-action px-3 py-1.5 text-[11px] font-semibold leading-4 text-auth-action-foreground no-underline sm:px-4 sm:py-2 sm:text-xs hover:opacity-90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-auth-accent focus-visible:ring-offset-2 focus-visible:ring-offset-auth-bg"
        >
          {t('app.auth.get_access')}
        </a>

        {/* The phone layout has no theme switch — on that size the theme
            follows the system. Hidden rather than removed so the desktop
            header, which does carry it, is untouched. */}
        <span className="hidden sm:block">
          <AuthThemeToggle />
        </span>
      </div>
    </header>
  );
};
